import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import RenderMonitor from '../components/RenderMonitor';
import { invalidateCache } from '../utils/cache';

const MAX_CONTEXT_LENGTH = 20000;

function CourseAIPage({ courseId }) {
  const navigate = useNavigate();
  const [courseName, setCourseName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [contextText, setContextText] = useState('');

  const snapshotKey = `course.${courseId}.snapshot.v1`;

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { data, error: fetchError } = await supabase
          .from('courses')
          .select('id, name, reflection_llm_context')
          .eq('id', parseInt(courseId, 10))
          .single();

        if (!mounted) return;
        if (fetchError) {
          setError(fetchError.message || 'Failed to load course AI content.');
          return;
        }
        setCourseName(data?.name || 'Course');
        setContextText(data?.reflection_llm_context ?? '');
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || 'Failed to load course AI content.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [courseId]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      if (contextText.length > MAX_CONTEXT_LENGTH) {
        setError(`Course AI content must be ${MAX_CONTEXT_LENGTH} characters or fewer.`);
        return;
      }

      const trimmed = contextText.trim();
      const { error: saveError } = await supabase
        .from('courses')
        .update({ reflection_llm_context: trimmed || null })
        .eq('id', parseInt(courseId, 10));

      if (saveError) {
        setError(saveError.message || 'Failed to save course AI content.');
        return;
      }
      invalidateCache(snapshotKey);
      setNotice('Course AI content saved.');
      setTimeout(() => navigate(`/course/${courseId}`), 400);
    } catch (e) {
      setError(e?.message || 'Failed to save course AI content.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="modern-loading">Loading course AI content…</div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <RenderMonitor componentName="CourseAIPage" />

      <button
        type="button"
        onClick={() => navigate(`/course/${courseId}`)}
        className="modern-button modern-button-secondary modern-mb-6"
        disabled={saving}
      >
        ← {courseName || 'Course'}
      </button>

      <div className="modern-card modern-mb-6">
        <h1 className="modern-title" style={{ marginBottom: 4 }}>Course AI</h1>
        <p className="modern-subtitle" style={{ marginBottom: 0 }}>
          Extra context appended to the LLM prompt for reflection follow-ups in this course.
        </p>
      </div>

      <div className="modern-card">
        <label htmlFor="course-ai-context" style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>
          Course AI content
        </label>
        <textarea
          id="course-ai-context"
          className="modern-textarea"
          rows={16}
          value={contextText}
          onChange={(e) => setContextText(e.target.value)}
          disabled={saving}
          placeholder="Enter course-specific context for the LLM…"
          style={{ width: '100%', resize: 'vertical', minHeight: 280 }}
        />
        <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
          {contextText.length.toLocaleString()} / {MAX_CONTEXT_LENGTH.toLocaleString()} characters
        </div>
        {error ? <div className="modern-error" style={{ marginTop: 12 }}>{error}</div> : null}
        {notice ? (
          <div style={{ marginTop: 12, color: '#15803d', fontSize: 14 }}>{notice}</div>
        ) : null}
        <div className="modern-flex modern-gap-3 modern-mt-4">
          <button
            type="button"
            className="modern-button modern-button-success"
            onClick={handleSave}
            disabled={saving || contextText.length > MAX_CONTEXT_LENGTH}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="modern-button modern-button-secondary"
            onClick={() => navigate(`/course/${courseId}`)}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default CourseAIPage;
